import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast, Toaster } from "sonner";

const UNITS = [
    { value: "ml", label: "ml · millilitres (liquids)" },
    { value: "gms", label: "gms · grams (powders, pills)" },
];

/** Build a display label from a numeric quantity + unit. Pad the number into
 *  a clean string so we get "30ml", "100gms", "1ltr"-style outputs. */
const buildLabel = (qty, unit) => {
    const n = parseFloat(qty);
    if (!Number.isFinite(n) || n <= 0) return "";
    // Strip trailing .0 so 30.0 → "30"
    const compact = Number.isInteger(n) ? String(n) : String(n);
    return `${compact}${unit}`;
};

export default function AdminSizes() {
    const [items, setItems] = useState([]);
    const [qty, setQty] = useState("");
    const [unit, setUnit] = useState("ml");

    const load = () => api.get("/sizes", { params: { active_only: false } }).then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const livePreview = useMemo(() => buildLabel(qty, unit), [qty, unit]);

    const add = async () => {
        const n = parseInt(qty, 10);
        if (!n || n <= 0 || n > 100000) return toast.error("Quantity must be between 1 and 100,000");
        try {
            await api.post("/sizes", { label: buildLabel(n, unit), ml: n, unit, active: true });
            setQty("");
            toast.success(`Added ${buildLabel(n, unit)}`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const toggle = async (s) => {
        await api.put(`/sizes/${s.id}`, { ...s, active: !s.active });
        load();
    };

    const del = async (id) => {
        if (!window.confirm("Delete this size? Existing products keep their variants — only new products lose access.")) return;
        await api.delete(`/sizes/${id}`);
        load();
    };

    return (
        <div data-testid="admin-sizes">
            <Toaster position="top-center" />
            <div className="label-eyebrow text-sky-700">Editable tabs</div>
            <h1 className="font-serif text-4xl text-stone-900 mt-2 mb-2">Bottle &amp; pack sizes</h1>
            <p className="text-sm text-stone-600 mb-8 max-w-2xl">
                Add any size you sell — liquids in <strong>ml</strong> (millilitres), powders / pills / globules in <strong>gms</strong> (grams). Disabled sizes are hidden from products but kept on file so historic invoices stay intact.
            </p>

            <div className="bg-white border border-stone-200 p-5 mb-6 max-w-2xl">
                <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                    <div>
                        <label className="text-xs uppercase tracking-widest text-stone-500">Quantity</label>
                        <Input
                            type="number"
                            min="1"
                            max="100000"
                            value={qty}
                            onChange={(e) => setQty(e.target.value)}
                            placeholder="e.g. 30 · 100 · 250"
                            className="rounded-none border-stone-300 mt-1"
                            data-testid="size-input"
                        />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-widest text-stone-500">Unit</label>
                        <Select value={unit} onValueChange={setUnit}>
                            <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="size-unit">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button
                        onClick={add}
                        disabled={!qty}
                        className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50 h-10"
                        data-testid="size-add"
                    >
                        <Plus className="w-4 h-4 mr-1" />Add
                    </Button>
                </div>
                {livePreview && (
                    <p className="text-[11px] text-stone-500 mt-3" data-testid="size-preview">
                        Preview: will appear in product variants as <strong className="font-mono text-stone-800">{livePreview}</strong>
                    </p>
                )}
            </div>

            <div className="bg-white border border-stone-200">
                {items.map((s) => (
                    <div key={s.id} className="border-b border-stone-100 last:border-b-0 p-4 flex items-center gap-4" data-testid={`size-row-${s.label}`}>
                        <div className="flex-1 font-mono text-stone-900">{s.label}</div>
                        <span className="text-xs uppercase tracking-widest text-stone-500">{s.ml} {s.unit || "ml"}</span>
                        <Switch checked={s.active} onCheckedChange={() => toggle(s)} data-testid={`size-toggle-${s.label}`} />
                        <Button variant="ghost" size="sm" onClick={() => del(s.id)} className="rounded-none text-red-700"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                ))}
                {items.length === 0 && <div className="p-8 text-center text-sm text-stone-500">No sizes yet — add one above.</div>}
            </div>
        </div>
    );
}
